'use client'

import React, { FC, ButtonHTMLAttributes, Ref } from 'react'
import { cn } from '../../utils/cn'

type SwitchButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onChange' | 'aria-labelledby' | 'aria-label'
>

type SwitchBaseProps = SwitchButtonProps & {
  isSelected: boolean
  onChange?: (isSelected: boolean) => void
  /** When set, names the switch via `aria-labelledby` (use with a visible label element). */
  labelledBy?: string
  className?: string
  ref?: Ref<HTMLButtonElement>
}

type SwitchProps = SwitchBaseProps &
  (
    | { label: string; 'aria-labelledby'?: never }
    | { label?: never; 'aria-labelledby': string; labelledBy?: never }
  )

export const Switch: FC<SwitchProps> = ({
  isSelected = false,
  onChange,
  label,
  labelledBy,
  className,
  disabled = false,
  ref,
  'aria-labelledby': ariaLabelledBy,
  ...props
}) => {
  const handleToggle = () => {
    if (!disabled && onChange) {
      onChange(!isSelected)
    }
  }

  const labelledById = labelledBy || ariaLabelledBy
  const nameProps = labelledById
    ? ({ 'aria-labelledby': labelledById } as const)
    : ({ 'aria-label': label } as const)

  return (
    <button
      type="button"
      disabled={disabled}
      ref={ref}
      className={cn(
        'relative inline-flex h-6 w-10 cursor-pointer items-center rounded-full bg-gray-200 transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300',
        {
          'bg-blue-600': isSelected,
          'cursor-not-allowed opacity-50': disabled
        },
        className
      )}
      {...props}
      role="switch"
      aria-checked={isSelected}
      {...nameProps}
      onClick={handleToggle}>
      <span
        className={cn(
          'inline-block size-4 transform rounded-full bg-white transition-transform duration-200 will-change-transform',
          isSelected ? 'translate-x-5' : 'translate-x-1'
        )}
        aria-hidden
      />
    </button>
  )
}
